import { Component, OnDestroy, OnInit } from '@angular/core';
import { Common, _ } from '@icc/shared/helpers';
import { StepComponent, ModalService } from '@icc/configurator/shared';
import { HardwareService } from '../../modules/door/hardware.service';
import { Subscription } from 'rxjs';
import { WindowDoorFacade } from '../../+state/window-door.facade';
import { PlatesPageComponent } from '../plates-page/plates-page.component';
import { CylindersPageComponent } from '../cylinders-page/cylinders-page.component';
import { map } from 'rxjs/operators';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { WindowActiveConfiguration } from '@icc/window-door/types';
import { ConfigurationsService } from '@icc/configurator/shared';

import { LocksService } from '../../modules/door/locks.service';
import { HandlesService } from '../../modules/handles/handles.service';
import { IccAccessoryAccessory } from '@icc/construction-elements/types';
import { HardwareSet } from '@icc/window-door/types';

interface HardwareSelectBox {
    img: string;
    name: string;
    color: any;
    colorId: string;
}

interface PlatesSelectBox {
    img: string;
    mainLockName: string;
    additionalLockName: string;
    color: any;
    colorId: string;
}

interface CylindersSelectBox {
    cylinders: {
        imagePath: string;
        mainLockName: string;
        additionalLockName: string;
        color: string;
        mainLockColor: string;
        additionalLockColor: string;
        system: string;
        electronicLock: string | null;
    }
}

interface PullSelectBox {
    pullType: {
        name: string;
        color: string;
        colorId: string;
        imagePath: string;
    }
}

interface LocksSelectBox {
    lock: {
        name: string;
    }
}

interface ElectronicLockSelectBox {
    electronicLockName: string;
    electronicLockColor?: string;
}

interface DoorViewerSelectBox {
    name: string;
    color?: string;
}

@Component({
    selector: 'icc-hardware',
    templateUrl: './hardware.component.html',
    styleUrls: ['./hardware.component.scss'],
    standalone: false
})
export class HardwareComponent extends StepComponent implements OnInit, OnDestroy {
    static stepName = _('DOOR|Osprzęt');
    static stepIcon = {
        ligature: 'lock',
    };
    public configurator = 'door';
    public stepId = 'hardware';
    public title = _('DOOR|Osprzęt');

    img = '';
    description = '';
    isPullColorRelatedToPlate: boolean;
    isRecessedPullColorsAvailable: boolean;

    selectedHardwareType$ = this.doorFacade.selectedHardwareType$;
    hardwareTypeBoxSelect: HardwareSelectBox | null = null;
    platesBoxSelect: PlatesSelectBox | null = null
    outerPlatesBoxSelect: PlatesSelectBox | null = null
    cylindersBoxSelect: CylindersSelectBox | null = null;
    showWindowLeverBox$ = this.doorFacade.showHardwareTypeBox$;
    pullBoxSelect: PullSelectBox | null = null;
    recessedPullBoxSelect: PullSelectBox | null = null;
    lockBoxSelect: LocksSelectBox | null = null;
    doorViewerBoxSelect: DoorViewerSelectBox | null = null;
    hardwareSet$ = this.doorFacade.hardwareSet$;
    hardwareSetSelect$ = this.doorFacade.hardwareSet$.pipe(map(value => value || 'none'));
    hardwareSets$ = this.doorFacade.hardwareSets$;
    pull$ = this.doorFacade.pull$;
    fillingWithRecessedPull$ = this.doorFacade.fillingWithRecessedPull$;
    lock$ = this.doorFacade.lock$;
    hinge$ = this.doorFacade.hinge$;
    hingeBoxSelect$ = this.doorFacade.doorHingeBoxSelect$;
    electronicLock$ = this.doorFacade.electronicLock$;
    electronicLockBoxSelect: ElectronicLockSelectBox | null = null;
    plateType$ = this.doorFacade.plateType$;

    plateMainLock$ = this.doorFacade.plateMainLock$;
    plateMainLockBoxSelect$ = this.doorFacade.plateMainLockBoxSelect$;
    plateAdditionalLock$ = this.doorFacade.plateAdditionalLock$;

    innerPlateMainLock$ = this.doorFacade.innerPlateMainLock$;
    innerPlateAdditionalLock$ = this.doorFacade.innerPlateAdditionalLock$;

    outerPlateMainLock$ = this.doorFacade.outerPlateMainLock$;
    outerPlateMainLockBoxSelect$ = this.doorFacade.outerPlateMainLockBoxSelect$;
    outerPlateAdditionalLock$ = this.doorFacade.outerPlateAdditionalLock$;

    doorViewer$ = this.doorFacade.doorViewer$;
    doorHandleAdjustment$ = this.doorFacade.doorHandleAdjustment$;
    doorViewerPunching$ = this.doorFacade.doorViewerPunching$;
    decorativeHingeCoversColor$ = this.doorFacade.decorativeHingeCoversColor$;
    showDecorativeHingeCovers$ = this.doorFacade.showDecorativeHingeCovers$;
    showDoorHandleAdjustmentBox$ = this.doorFacade.showDoorHandleAdjustmentBox$;
    showDoorViewer$ = this.doorFacade.showDoorViewer$;
    showDoorViewerPunchingOption$ = this.doorFacade.showDoorViewerPunchingOption$;
    contextInfoInDoorViewer$ = this.doorFacade.getContextInfoInDoorViewer$;
    cylinderAdditionalLock$ = this.doorFacade.cylinderAdditionalLock$;

    cylinderAdditionalLock: boolean;
    electronicLock: boolean;
    plateAdditionalLock: boolean;
    innerPlateAdditionalLock: boolean;
    outerPlateAdditionalLock: boolean;
    isKeyAvailableForMainLock: boolean;
    hardwareSets: HardwareSet[] = [];
    hardwareSetSelect: HardwareSet;
    integratedPull = false;

    private subscriptions: Subscription[] = [];

    static stepEnable = (conf: WindowActiveConfiguration) =>
        conf.type === 'door' && (Boolean(conf.System.door_type) || conf.System.door_hardware_type === 'hardware');

    constructor(
        private hardwareService: HardwareService,
        private doorFacade: WindowDoorFacade,
        private modalService: ModalService,
        private configurationsService: ConfigurationsService<'door'>,
        private locksService: LocksService,
        private handlesService: HandlesService,
    ) {
        super();
    }

    ngOnInit() {
        const conf = this.configurationsService.conf.Current;
        this.hardwareSets = this.hardwareService.hardwareSets;

        this.subscriptions.push(this.doorFacade.hardwareTypeBoxSelect$.subscribe((box) => {
            this.hardwareTypeBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.platesBoxSelect$.subscribe((box) => {
            this.platesBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.outerPlatesBoxSelect$.subscribe((box) => {
            this.outerPlatesBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.cylindersBoxSelect$.subscribe((box) => {
            this.cylindersBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.pullsBoxSelect$.subscribe((box) => {
            this.pullBoxSelect = box;
            this.isPullColorRelatedToPlate = this.configurationsService.conf.Current.doorHardware.pullColorRelatedToPlate;
        }));

        this.subscriptions.push(this.doorFacade.recessedPullsBoxSelect$.subscribe((box) => {
            this.recessedPullBoxSelect = box;
            this.isRecessedPullColorsAvailable = this.hardwareService.getRecessedPullColors().length > 0;
        }));

        this.subscriptions.push(this.doorFacade.locksBoxSelect$.subscribe((box) =>{
            this.lockBoxSelect = box;
        }))

        this.subscriptions.push(this.doorFacade.electronicLockBoxSelect$.subscribe((box) => {
            this.electronicLockBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.doorViewerBoxSelect$.subscribe((box) => {
            this.doorViewerBoxSelect = box;
        }));

        this.subscriptions.push(this.doorFacade.plateAdditionalLock$.subscribe((plateAdditionalLock) => {
            this.plateAdditionalLock = plateAdditionalLock;
        }));

        this.subscriptions.push(this.doorFacade.innerPlateAdditionalLock$.subscribe((innerPlateAdditionalLock) => {
            this.innerPlateAdditionalLock = innerPlateAdditionalLock;
        }));

        this.subscriptions.push(this.doorFacade.outerPlateAdditionalLock$.subscribe((outerPlateAdditionalLock) => {
            this.outerPlateAdditionalLock = outerPlateAdditionalLock;
        }));

        this.subscriptions.push(this.doorFacade.cylinderAdditionalLock$.subscribe((cylinderAdditionalLock) => {
            this.cylinderAdditionalLock = cylinderAdditionalLock;
        }));

        this.subscriptions.push(this.doorFacade.electronicLock$.subscribe((electronicLock) => {
            this.electronicLock = electronicLock;
        }));

        this.subscriptions.push(this.doorFacade.hardwareSets$.subscribe((hardwareSets) => {
            this.hardwareSets = this.hardwareSets.filter(hardwareSet => hardwareSets.some(hs => hardwareSet.setId === hs));
        }));
        this.subscriptions.push(this.doorFacade.hardwareSet$.subscribe((selectHardwareSet) => {
            this.hardwareSetSelect = this.hardwareSets.find(hardwareSet => hardwareSet.setId === selectHardwareSet);
        }));

        this.subscriptions.push(this.doorFacade.fillingWithIntegratePull$.subscribe((fillingWithIntegratePull) => {
            this.integratedPull = fillingWithIntegratePull;
        }));

        this.isKeyAvailableForMainLock = conf.doorHardware.cylinders.isKeyAvailableForMainLock;

    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    changeTypeOptions() {
        this.hardwareService.openModalTypeOptions();
    }

    selectType() {
        this.hardwareService.openModalType();
    }

    openPlatesOptions(type: 'inner' | 'outer' = 'inner') {
        if(
            (type === 'inner' && this.configurationsService.conf.Current.doorHardware.set !== 'mixedLever' && this.plateAdditionalLock)
            || (type === 'inner' && this.configurationsService.conf.Current.doorHardware.set === 'mixedLever' && this.innerPlateAdditionalLock)
            || (type === 'outer' && this.outerPlateAdditionalLock)
        ) {
            this.modalService.open({
                pageComponent: PlatesPageComponent,
                resolve: {
                    type: () => type,
                },
            });
        } else {
            this.hardwareService.openPlateToMainLockModal(type);
        }
    }

    changePlateToMainLockOptions(type: 'inner' | 'outer' = 'inner') {
        this.hardwareService.openModalPlateOptions(type);
    }

    openPullsOptions() {
        this.hardwareService.openModalPullOptions();
    }

    changeLock() {
        this.hardwareService.openLockModal();
    }

    changePull() {
        this.hardwareService.openPullsModal();
    }

    changeRecessedPull() {
        this.hardwareService.openRecessedPullsModal();
    }

    openRecessedPullOptions() {
        this.hardwareService.openModalRecessedPullOptions();
    }

    changeHardwareSet() {
        this.hardwareService.openHardwareSetModal();
    }

    isChangeHardwareSetDisabled() {
       return this.hardwareSets.length <= 1;
    }

    isDoorHandleAdjustmentDisabled() {
        const selectedId = this.configurationsService.conf.Current.doorHardware.doorHandleAdjustment;
        return !this.hardwareService.doorHandleAdjustments.some(item => Number(item.id) !== Number(selectedId));
    }

    isHardwareTypeDisabled() {
        const conf = this.configurationsService.conf.Current;
        const types = this.hardwareService.getTypes(conf);
        const selectedType = types.find(type => Number(type.id) === Number(conf.doorHardware.type));
        return !types.some(type => Number(type.id) !== Number(conf.doorHardware.type))
            && this.getAvailableColorsCount(selectedType) <= 1;
    }

    isElectronicLockDisabled() {
        const conf = this.configurationsService.conf.Current;
        const selectedId = conf.doorHardware.electronicLock?.id;
        const selectedLock = this.hardwareService.electronicLocks.find(lock => Number(lock.id) === Number(selectedId));
        return !this.hardwareService.electronicLocks.some(lock => Number(lock.id) !== Number(selectedId))
            && this.getAvailableColorsCount(selectedLock) <= 1;
    }

    isPlateTypeDisabled() {
        const conf = this.configurationsService.conf.Current;
        return !this.hardwareService.getPlateTypes(conf)
            .some(type => Number(type.id) !== Number(conf.doorHardware.plateType));
    }

    openCylindersOptions() {
        if(this.cylinderAdditionalLock || this.electronicLock) {
            this.modalService.open({
                pageComponent: CylindersPageComponent
            })
        } else {
            this.hardwareService.openCylinderToMainLockModal();
        }
    }

    changeCylinderToMainLockOptions() {
        this.hardwareService.openModalCylinderToMainLockOptions();
    }

    changeElectronicLock() {
        this.hardwareService.openElectronicLockModal();
    }

    changePlateType() {
        this.hardwareService.openPlateTypeModal();
    }

    changeDoorViewer() {
        this.hardwareService.openDoorViewerModal();
    }
    
    changeDoorViewerOptions() {
        this.hardwareService.openDoorViewerOptionsModal();
    }

    changeDoorHandleAdjustment() {
        this.hardwareService.openDoorHandleAdjustmentsModal();
    }

    changeDoorViewerPunching($event: MatSlideToggleChange) {
        this.hardwareService.changeDoorViewerPunching($event.checked);
    }

    changeDecorativeHingeCover() {
        this.hardwareService.openModalDecorativeHingeCoverOptions();
    }

    changeHinges() {
        this.handlesService.openModalHinge();
    }

    isPullButtonDisabled() {
        const handleType = this.configurationsService.conf.Current.doorHardware.set === 'leverPull' ? 'pull' : 'doublePull';
        const pulls = this.hardwareService?.pulls?.filter(pull => pull.handle_type === handleType);
        const pullsLength = pulls?.length;

        const selectedPull = pulls?.find(pull => Number(pull.id) === Number(this.configurationsService.conf.Current.doorHardware?.pull));
        return pullsLength <= 1 && this.getAvailableColorsCount(selectedPull) <= 1;
    }

    isPullButton2Disabled() {
        const pullId = this.configurationsService.conf.Current.doorHardware?.pull;
        const pulls = this.hardwareService?.pulls;
        const selectedPull = pulls.find(p => Number(p.id) === Number(pullId));
        const pullColorLength = selectedPull?.colors_ids?.length;

        if(!Common.isDefined(pullColorLength) || pullColorLength <= 1) {
            return true;
        }

        return false;
    }

    isRecessedPullButtonDisabled() {
        const recessedPulls = this.hardwareService.getRecessedPulls()
        return recessedPulls.length <= 1;
    }

    isRecessedPullButton2Disabled() {
        const recessedPullColors = this.hardwareService.getRecessedPullColors();
        return recessedPullColors.every(color => color.colors.length <= 1);
    }

    isDoorViewerButtonDisabled() {
        const doorViewers = this.hardwareService.getAvailableDoorViewers()
        const doorViewersLength = doorViewers?.length;
        const selectedId = this.configurationsService.conf.Current.doorHardware?.doorViewer;
        const selectedDoorViewer = doorViewers.find(viewer => Number(viewer.id) === Number(selectedId));

        return doorViewersLength <= 1 && this.getAvailableColorsCount(selectedDoorViewer) <= 1;
    }

    isDoorViewerButton2Disabled() {
        const doorViewerId = this.configurationsService.conf.Current.doorHardware?.doorViewer;
        const doorViewers = this.hardwareService?.doorViewers;
        const selectedDoorViewer = doorViewers.find(p => Number(p.id) === Number(doorViewerId));
        const doorViewerColorLength = selectedDoorViewer?.colors_ids?.length;

        return !Common.isDefined(doorViewerColorLength) || doorViewerColorLength <= 1;
    }

    getDoorViewerImageUrl() {
        const conf = this.configurationsService.conf.Current;
        const doorViewerId = conf.doorHardware?.doorViewer;
        const doorViewer = this.hardwareService?.doorViewers?.find(p => Number(p.id) === Number(doorViewerId));
        
        if (!doorViewer?.img) {
            return '/img/door/wizjer_brak.jpg';
        }

        const colorId = conf.doorHardware?.doorViewerColor;
        
        if (doorViewer.colors_imgs && colorId && doorViewer.colors_imgs[colorId]) {
            return '/files/windowhandlescolorswindowaccessory/' + doorViewer.colors_imgs[colorId];
        }
        
        return '/files/windowaccessory/' + doorViewer.img;
    }

    isLockButtonDisabled() {
        const locks = this.locksService?.locks;
        if(locks?.length > 1) {
            return false;
        }

        return true;
    }

    isCylinderBoxVisible() {
        const cylindersBox = this.cylindersBoxSelect?.cylinders;
        const box = cylindersBox && Object.values(cylindersBox).filter(x => (x !== '' && x !== null));

        if(box?.length > 0) {
            return true;
        }

        return false;
    }

    isDecorativeHingeCoverButtonDisabled(conf = this.configurationsService.conf.Current) {
        const decoHingeCovers = this.hardwareService.decoHingeCovers;

        const hingeCoverId = conf.doorHardware && conf.doorHardware.decorativeHingeCovers;
        const cover = decoHingeCovers.find(f => Number(f.id) === Number(hingeCoverId));

        const colorLength = cover?.colors_ids?.length || 0;

        if(conf.doorHardware.decorativeHingeCoversColorRelatedToPlate || colorLength <= 1) {
            return true;
        }
        return false;
    }

    isHingeButtonDisabled(conf = this.configurationsService.conf.Current) {
        const hingeId = conf.Hinge?.id;

        const hinges = this.handlesService.findHingesBySystem(conf.System);
        const selectedHinge = hinges.find((h) => Number(h.id) === Number(hingeId));
        const hingeColorLength = selectedHinge.colors_ids?.length;
        if ((!hingeColorLength || hingeColorLength <= 1) && (!hinges || hinges.length <= 1)) {
            return true;
        }

        return false;
    }

    isPlatesButtonDisabled(type: 'inner' | 'outer' = 'inner') {
        const platesToMainLock = type === 'inner'
            ? this.hardwareService.getPlatesToMainLock()
            : this.hardwareService.getOuterPlatesToMainLock();
        const platesToAdditionalLock = type === 'inner'
            ? this.hardwareService.getPlatesToAdditionalLock()
            : this.hardwareService.getOuterPlatesToAdditionalLock();

        return platesToMainLock?.length <= 1 && platesToAdditionalLock?.length <= 1
    }

    isPlatesToMainLockButton2Disabled(type: 'inner' | 'outer' = 'inner', conf = this.configurationsService.conf.Current) {
        const plateId = type === 'inner'
            ? conf.doorHardware?.plates?.mainLock
            : conf.doorHardware?.outerPlates?.mainLock;
        const platesToMainLock = type === 'inner'
            ? this.hardwareService.getPlatesToMainLock()
            : this.hardwareService.getOuterPlatesToMainLock();
        const selectedPlate = platesToMainLock.find(p => Number(p.id) === Number(plateId));
        const plateColorsLength = selectedPlate?.colors_ids?.length;

        return plateColorsLength <= 1;
    }

    isCylinderMainLockButtonDisabled() {
        const cylindersToMainLock = this.hardwareService.getCylindersToMainLock();
        const cylindersToAdditionalLock = this.hardwareService.getCylindersToAdditionalLock();
        const cylindersToMainLockLength = cylindersToMainLock.length;
        const cylindersToAdditionalLockLength = cylindersToAdditionalLock.length;

        return cylindersToMainLockLength <= 1 && cylindersToAdditionalLockLength <= 1;
    }

    isCylinderMainLockButton2Disabled(conf = this.configurationsService.conf.Current) {
        const cylinderId = conf.doorHardware?.cylinders?.mainLock;
        const cylindersToMainLock = this.hardwareService.getCylindersToMainLock();
        const selectedCylinderToMainLock = cylindersToMainLock.find(p => Number(p.id) === Number(cylinderId));
        const cylinderColorsLength = selectedCylinderToMainLock?.colors_ids?.length;
        return cylinderColorsLength <= 1 && !selectedCylinderToMainLock.additional_key_for_the_cylinder;
    }

    private getAvailableColorsCount(item: { colors_ids?: Array<string | number> } | null | undefined) {
        const availableColorIds = new Set(this.hardwareService.hardwareColors.map(color => Number(color.id)));
        return (item?.colors_ids || [])
            .filter((id, index, ids) => ids.indexOf(id) === index && availableColorIds.has(Number(id)))
            .length;
    }
}

